// Graduation (play-modes spec §10): after a local win the game suggests
// the next preset — "You've mastered Explorer! Try Apprentice: it adds
// fog of war and diplomacy" — and a player who then *plays* a graduated
// preset is drip-fed its new modules, one per match, as a guided "new
// rule" cinematic (the pacing spec names the chapter card as the other
// natural moment; until chapter cards ship, the moment rides the
// cinematic director at match start).
//
// All state is per-browser localStorage (with try/catch — a stale or
// unavailable store must never block a win screen):
//   aod.graduation.mastered.v1.<id>   the player has won this preset
//   aod.graduation.suggested.v1.<id>  the "try the next" card was shown
//   aod.graduation.announced.v1.<id>  how many of its new rules were shown
//
// Pure decision functions are separated from storage so they can be unit
// tested without a DOM.

import { MODULE_IDS, type ModuleId, PRESETS, presetById } from '@aod/protocol';

export type LadderId = (typeof PRESETS)[number]['id'];

/**
 * The difficulty ladder, in order. Deliberately explicit: presets such as
 * `seafarers` and `royal-houses` are flavour side-quests, not rungs — appending
 * one to PRESETS must not silently make the Champions preset "graduate" into it
 * (owner's note, 2026-10-22).
 */
const LADDER: readonly LadderId[] = [
  'explorer',
  'apprentice',
  'standard',
  'strategist',
  'grand',
  'highlands',
  'legends',
];

const PREFIX = 'aod.graduation';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / disabled storage: graduation state is a nicety and
    // never blocks a win.
  }
}

/** True when `id` is one of the ladder presets (not `custom`, not a side-quest). */
export function isLadderId(id: string): id is LadderId {
  return LADDER.includes(id as LadderId);
}

/** The next preset up the ladder, or null at the top (or for custom / side-quests). */
export function nextPresetId(id: string): LadderId | null {
  const i = LADDER.indexOf(id as LadderId);
  if (i < 0) return null;
  return LADDER[i + 1] ?? null;
}

/**
 * Modules `to` has that `from` lacks — what graduating to `to` adds —
 * in `MODULE_IDS` order so both the victory copy and the drip sequence
 * are stable.
 */
export function presetDelta(from: string, to: string): readonly ModuleId[] {
  const fromMods = new Set<string>(presetById(from as LadderId).ruleset.modules);
  const toMods = presetById(to as LadderId).ruleset.modules;
  return MODULE_IDS.filter((m) => toMods.includes(m) && !fromMods.has(m));
}

export function isMastered(id: string): boolean {
  return read(`${PREFIX}.mastered.v1.${id}`) === '1';
}

export function markMastered(id: string): void {
  write(`${PREFIX}.mastered.v1.${id}`, '1');
}

/** Whether the "try the next preset" card has already been shown for a win of `from`. */
export function isSuggested(from: string): boolean {
  return read(`${PREFIX}.suggested.v1.${from}`) === '1';
}

export function markSuggested(from: string): void {
  write(`${PREFIX}.suggested.v1.${from}`, '1');
}

/** How many of `to`'s new rules have been announced so far. */
export function announcedCount(to: string): number {
  const raw = read(`${PREFIX}.announced.v1.${to}`);
  const n = raw === null ? Number.NaN : Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function markAnnounced(to: string, count: number): void {
  write(`${PREFIX}.announced.v1.${to}`, String(count));
}

export interface NewRulePlan {
  /** The next unannounced module the preset adds over its predecessor. */
  readonly module: ModuleId;
  /** Index of `module` in the delta — the caller stores `announced + 1`. */
  readonly announced: number;
}

/**
 * The guided new-rule moment for a match about to start under
 * `matchPreset` (spec §10 "unlock one module at a time"): null unless
 * the player has *graduated* the previous preset (won it — a preset
 * hopper who never mastered the predecessor gets no drip) and unannounced
 * modules remain.
 */
export function newRulePlan(matchPreset: string): NewRulePlan | null {
  const i = PRESETS.findIndex((p) => p.id === matchPreset);
  // Custom (or unknown) presets and the ladder root have no predecessor.
  if (i <= 0) return null;
  const prev = PRESETS[i - 1];
  if (!prev) return null; // unreachable; indexed access is checked
  if (!isMastered(prev.id)) return null;
  const delta = presetDelta(prev.id, matchPreset);
  const announced = announcedCount(matchPreset);
  const module = delta[announced]; // undefined once the drip is exhausted
  return module === undefined ? null : { module, announced };
}
